import React from 'react'
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import './DashboardPieChart.style.css'

export type DashboardPieChartSlice = {
  name: string
  value: number
  color: string
}

type DashboardPieChartProps = {
  /** Pie segments (name, value, color). */
  data: DashboardPieChartSlice[]
  /** Chart size in px (width and height). Default 220. */
  size?: number
  /** Inner radius as % or number for donut (e.g. "55%"). Omit for full pie. */
  innerRadius?: string | number
  /** Show legend below the chart. Default true. */
  showLegend?: boolean
  /** Optional formatter for tooltip value (e.g. (v) => `${v}%`). */
  valueFormatter?: (value: number) => string
  className?: string
}

const defaultValueFormatter = (v: number): string => String(v)

function DashboardPieChart({
  data,
  size = 220,
  innerRadius,
  showLegend = true,
  valueFormatter = defaultValueFormatter,
  className = '',
}: DashboardPieChartProps): React.ReactElement {
  return (
    <div className={`dashboard-pie-chart ${className}`}>
      <div
        className="dashboard-pie-chart-wrap"
        style={{ width: size, height: size }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart>
            <Tooltip
              formatter={(value: number) => [valueFormatter(value), '']}
              contentStyle={{
                padding: '8px 12px',
                fontSize: 12,
                borderRadius: 8,
                border: '1px solid var(--gray-200)',
                background: 'var(--gray-white)',
              }}
            />
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius="80%"
              paddingAngle={2}
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${entry.name}-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
      {showLegend && data.length > 0 && (
        <div className="dashboard-pie-chart-legend">
          {data.map((slice) => (
            <div key={slice.name} className="dashboard-pie-chart-legend-item">
              <span
                className="dashboard-pie-chart-legend-dot"
                style={{ backgroundColor: slice.color }}
              />
              <span className="dashboard-pie-chart-legend-label">
                {slice.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default DashboardPieChart
